import { toast } from "$global-providers/toast-provider";
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { EpocusUserStatus } from "elephant-web/gql";
import { useState } from "react";
import { Link } from "react-router-dom";
import { linkToEpocusEditUser } from "../../../links";
import {
  useEpocusResetUserMfaMutation,
  useEpocusSendUserPasswordResetMutation,
  useEpocusSetUserEnabledMutation,
} from "./__generated__/program-users-operations.gql";

type ConfirmAction = "reset-mfa" | "toggle-enabled" | null;

export const ProgramUserRowMenu: FC<{
  epocusUserId: string;
  status: EpocusUserStatus;
}> = ({ epocusUserId, status }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const [sendPasswordReset] = useEpocusSendUserPasswordResetMutation();
  const [resetMfa] = useEpocusResetUserMfaMutation();
  const [setEnabled] = useEpocusSetUserEnabledMutation();

  const enabled = status !== EpocusUserStatus.Disabled;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSendPasswordReset = () => {
    handleClose();
    sendPasswordReset({ variables: { epocusUserId } })
      .then(() => toast.success("Password reset email sent"))
      .catch(() => toast.error("Couldn't send the password reset email"));
  };

  const handleConfirmResetMfa = () => {
    setConfirmAction(null);
    resetMfa({ variables: { epocusUserId } })
      .then(() => toast.success("MFA reset for this user"))
      .catch(() => toast.error("Couldn't reset MFA for this user"));
  };

  const handleConfirmToggleEnabled = () => {
    setConfirmAction(null);
    setEnabled({
      variables: { epocusUserId, enabled: !enabled },
      refetchQueries: ["EpocusProgramUsers"],
    })
      .then(() =>
        toast.success(enabled ? "Account disabled" : "Account enabled"),
      )
      .catch(() => toast.error("Couldn't update this account's status"));
  };

  return (
    <>
      <IconButton aria-label="User actions" onClick={handleOpen} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={handleClose}
          component={Link}
          to={linkToEpocusEditUser(epocusUserId)}
        >
          Edit user
        </MenuItem>
        <MenuItem onClick={handleSendPasswordReset}>
          Send password reset
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setConfirmAction("reset-mfa");
          }}
        >
          Reset MFA
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setConfirmAction("toggle-enabled");
          }}
        >
          {enabled ? "Disable account" : "Enable account"}
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmAction === "reset-mfa"}
        onClose={() => setConfirmAction(null)}
      >
        <DialogTitle>Reset MFA for this user?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button onClick={handleConfirmResetMfa} color="primary">
            Reset MFA
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmAction === "toggle-enabled"}
        onClose={() => setConfirmAction(null)}
      >
        <DialogTitle>
          {enabled
            ? "Disable this user's account?"
            : "Enable this user's account?"}
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button onClick={handleConfirmToggleEnabled} color="primary">
            {enabled ? "Disable" : "Enable"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
